import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import { useTeamStore } from '../../stores/teamStore';
import { useEventStore } from '../../stores/eventStore';
import type { Event, EventType } from '../../types/event';
import type { Setlist } from '../../types/setlist';
import { setlistsApi } from '../../api/setlists';
import EventModal from './components/EventModal';
import EventTypeManager from './components/EventTypeManager';
import GenerateEventsModal from './components/GenerateEventsModal';

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-500/20 text-blue-300',
  COMPLETED: 'bg-green-500/20 text-green-300',
  CANCELLED: 'bg-red-500/20 text-red-300',
};

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Agendado',
  COMPLETED: 'Realizado',
  CANCELLED: 'Cancelado',
};

export default function EventsPage() {
  const { activeTeamId, teamMembers, loadTeamMembers } = useTeamStore();
  const {
    events,
    eventTypes,
    loadEvents,
    loadEventTypes,
    addEvent,
    editEvent,
    removeEvent,
    addEventType,
    removeEventType,
    generateEvents,
  } = useEventStore();

  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showTypesPanel, setShowTypesPanel] = useState(false);
  const [generateTypeId, setGenerateTypeId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [setlists, setSetlists] = useState<Setlist[]>([]);

  const teamId = activeTeamId ?? '';

  // Determine current user role
  const members = teamId ? (teamMembers[teamId] ?? []) : [];
  const canEdit = members.length === 0 || ['ADMIN', 'LEADER'].includes(members[0]?.role ?? '');

  useEffect(() => {
    if (!teamId) return;
    loadEvents(teamId, filterStatus ? { status: filterStatus } : undefined);
    loadEventTypes(teamId);
    loadTeamMembers(teamId);
    setlistsApi.list(teamId).then((r) => setSetlists(r.data)).catch(() => {});
  }, [teamId, filterStatus, loadEvents, loadEventTypes, loadTeamMembers]);

  const handleSaveEvent = async (payload: Parameters<typeof addEvent>[1]) => {
    if (editingEvent) {
      await editEvent(teamId, editingEvent.id, payload);
    } else {
      await addEvent(teamId, payload);
    }
  };

  const handleCancel = async (event: Event) => {
    if (!confirm(`Cancelar "${event.name}"?`)) return;
    await removeEvent(teamId, event.id);
  };

  const handleGenerate = async (typeId: string, startDate: string, endDate: string) => {
    const result = await generateEvents(teamId, typeId, startDate, endDate);
    return result.length;
  };

  const generateType = generateTypeId
    ? (eventTypes.find((t) => t.id === generateTypeId) as EventType | undefined) ?? null
    : null;

  const visibleEvents = filterStatus
    ? events.filter((e) => e.status === filterStatus)
    : events;

  if (!teamId) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/40">Selecione uma equipe para ver os eventos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex flex-col pt-16">
      <Navbar />

      {/* Header */}
      <div className="px-6 md:px-10 py-6 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Eventos</h1>
            <p className="text-white/30 text-sm mt-1">Gerencie os eventos da equipe</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTypesPanel((v) => !v)}
              className={`px-4 py-2 border rounded-xl text-sm transition-colors ${
                showTypesPanel
                  ? 'border-[#C8A96E]/50 text-[#C8A96E]'
                  : 'border-white/10 text-white/60 hover:text-white'
              }`}
            >
              Tipos
            </button>
            {canEdit && (
              <button
                onClick={() => {
                  setEditingEvent(null);
                  setShowEventModal(true);
                }}
                className="px-4 py-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm hover:bg-[#D4B87A] transition-colors"
              >
                + Evento
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-4">
          {['', 'SCHEDULED', 'COMPLETED', 'CANCELLED'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${
                filterStatus === s
                  ? 'bg-[#C8A96E]/20 text-[#C8A96E] border border-[#C8A96E]/30'
                  : 'text-white/40 hover:text-white/70 border border-transparent'
              }`}
            >
              {s === '' ? 'Todos' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Event list */}
        <main className="flex-1 overflow-y-auto px-6 md:px-10 py-6">
          {visibleEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                <i className="ri-calendar-line text-2xl text-white/20" />
              </div>
              <p className="text-white/40 text-sm">Nenhum evento encontrado</p>
              {canEdit && (
                <button
                  onClick={() => {
                    setEditingEvent(null);
                    setShowEventModal(true);
                  }}
                  className="mt-4 text-[#C8A96E] text-sm hover:text-[#D4B87A]"
                >
                  Criar primeiro evento
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {visibleEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-2xl px-5 py-4 hover:border-white/20 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[3rem]">
                      <p className="text-xs text-white/30 uppercase">
                        {new Date(event.date + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'short' })}
                      </p>
                      <p className="text-xl font-bold text-white leading-none">
                        {new Date(event.date + 'T12:00:00').getDate()}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-white">{event.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {event.event_type_name && (
                          <span className="text-xs text-white/40">{event.event_type_name}</span>
                        )}
                        {event.start_time && (
                          <span className="text-xs text-white/30">
                            {event.start_time.slice(0, 5)}
                            {event.end_time ? `–${event.end_time.slice(0, 5)}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS_COLORS[event.status]}`}>
                      {STATUS_LABELS[event.status]}
                    </span>
                    {canEdit && event.status !== 'CANCELLED' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => {
                            setEditingEvent(event);
                            setShowEventModal(true);
                          }}
                          className="p-1.5 text-white/30 hover:text-white transition-colors"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => handleCancel(event)}
                          className="p-1.5 text-white/30 hover:text-red-400 transition-colors"
                        >
                          <i className="ri-close-line text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Types sidebar */}
        {showTypesPanel && (
          <aside className="w-80 border-l border-white/5 px-6 py-6 overflow-y-auto">
            <EventTypeManager
              eventTypes={eventTypes}
              onAdd={async (p) => { await addEventType(teamId, p); }}
              onDelete={(id) => removeEventType(teamId, id)}
              onGenerateOpen={(id) => setGenerateTypeId(id)}
            />
          </aside>
        )}
      </div>

      {showEventModal && (
        <EventModal
          teamId={teamId}
          eventTypes={eventTypes}
          setlists={setlists}
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={() => {
            setShowEventModal(false);
            setEditingEvent(null);
          }}
        />
      )}

      {generateType && generateTypeId && (
        <GenerateEventsModal
          typeId={generateTypeId}
          typeName={generateType.name}
          onGenerate={handleGenerate}
          onClose={() => setGenerateTypeId(null)}
        />
      )}
    </div>
  );
}
